import React, { useState, useEffect } from 'react';
import './MembersListPage.css';
import { useAuth } from './AuthContext';

interface Member {
  id: number; // Keep the original ID for the key prop
  member_id: string;
  sponsor_id: string | null;
  sponsor_name: string | null;
  member_name: string | null;
  joining_date: string | null;
}


const MembersListPage: React.FC = () => {
  const { user } = useAuth();
  const [members, setMembers] = useState<Member[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [memberTypeFilter, setMemberTypeFilter] = useState<string>('all');

  // --- Server-side Pagination State ---
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMembers, setTotalMembers] = useState(0);
  const [goToPage, setGoToPage] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // PDF Generation State
  const [pdfMember, setPdfMember] = useState<Member | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  // --- Search Column Selection State ---
  const [searchColumns, setSearchColumns] = useState({
    id: true,
    memberId: true,
    memberName: true,
    sponsorId: true,
    sponsorName: true,
  });
  useEffect(() => {
    const fetchMembers = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Construct the URL with query parameters for pagination and search
        const params = new URLSearchParams({
          page: String(currentPage),
          pageSize: String(itemsPerPage),
          search: searchTerm,
        });
        if (memberTypeFilter !== 'all') {
          params.append('type', memberTypeFilter);
        }
        if (startDate) {
          params.append('startDate', startDate);
        }
        if (endDate) {
          params.append('endDate', endDate);
        }
        // Append search column flags if a search term exists
        if (searchTerm) {
          if (searchColumns.id) {
            params.append('searchInId', 'true');
          }
          if (searchColumns.memberId) {
            params.append('searchInMemberId', 'true');
          }
          if (searchColumns.memberName) {
            params.append('searchInMemberName', 'true');
          }
          if (searchColumns.sponsorId) {
            params.append('searchInSponsorId', 'true');
          }
          if (searchColumns.sponsorName) {
            params.append('searchInSponsorName', 'true');
          }
        }

        const response = await fetch(`/api/members?${params.toString()}`, { credentials: 'include' });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch members');
        }

        const payload = await response.json();
        setMembers(payload.data);
        setTotalPages(payload.pagination.totalPages);
        setTotalMembers(payload.pagination.totalMembers);
      } catch (err) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('An unknown error occurred.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchMembers();
  }, [currentPage, itemsPerPage, searchTerm, startDate, endDate, searchColumns, memberTypeFilter]);

  // Reset visible count when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage, searchTerm, startDate, endDate, searchColumns, memberTypeFilter]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleGoToPageSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const page = parseInt(goToPage, 10);
    if (!isNaN(page)) {
      handlePageChange(page);
    }
    setGoToPage('');
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, checked } = e.target;
      setSearchColumns(prev => ({ ...prev, [name]: checked }));
  };

  const handleGeneratePdf = async (member: Member) => {
    if (!member) return;
    setPdfError(null);

    try {
      // Show some loading indicator if you have one
      // For simplicity, we'll just proceed

      const response = await fetch('/api/members/welcome-letter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          memberId: member.member_id,
          memberName: member.member_name || member.member_id,
          joiningDate: member.joining_date
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to generate welcome letter.' }));
        throw new Error(errorData.message);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `welcome_${member.member_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'An unknown error occurred.';
      setPdfError(errorMessage);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchTerm(searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
  };

  if (isLoading) return <div className="members-list-container"><h2>Members List</h2><p>Loading...</p></div>;
  if (error) return <div className="members-list-container"><h2>Members List</h2><div className="error-message">{error}</div></div>;

  return (
    <div className="members-list-container">
      <div className="list-header">
        <div className="header-left">
          <h2>Members List - 
          {totalMembers > 0 && <span className="total-count">{totalMembers}</span>}
        </h2>
        </div>
        <div className="header-right">
          <div className="toolbar-row">
            <form onSubmit={handleSearchSubmit} className="search-form members-list-search">
              <div className="search-input-wrapper">
                <input
                  type="text"
                  placeholder="Search selected columns..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="search-input"
                />
                {searchInput && (
                  <button type="button" onClick={handleClearSearch} className="clear-search-button">&times;</button>
                )}
              </div>
              <button type="submit" className="search-button">Search</button>
            </form>
            <div className="filter-group">
              <label htmlFor="memberTypeFilter">Type:</label>
              <select id="memberTypeFilter" value={memberTypeFilter} onChange={(e) => setMemberTypeFilter(e.target.value)} className="filter-select">
                <option value="all">All Members</option>
                <option value="direct">Direct</option>
                <option value="self">Self</option>
                <option value="self_from_direct">Self from Direct</option>
              </select>
            </div>
              <div className="filter-group date-filter-group">
                <div className="date-filter-item">
                  <label htmlFor="startDate">From:</label>
                  <input
                    type="date"
                    id="startDate"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="date-input"
                  />
                </div>
                <div className="date-filter-item">
                  <label htmlFor="endDate">To:</label>
                  <input type="date" id="endDate" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="date-input" />
                </div>
              </div>
          </div>
        </div>
      </div>
      <table className="members-table">
        <thead>
          <tr>
            <th className="sno-column">S.No.</th>
            <th>
              <label>
                <input type="checkbox" name="id" checked={searchColumns.id} onChange={handleCheckboxChange} />
                ID
              </label>
            </th>
            <th>
              <label>
                <input type="checkbox" name="memberId" checked={searchColumns.memberId} onChange={handleCheckboxChange} />
                Member ID
              </label>
            </th>
            <th>
              <label>
                <input type="checkbox" name="memberName" checked={searchColumns.memberName} onChange={handleCheckboxChange} />
                Member Name
              </label>
            </th>
            <th>
              <label>
                <input type="checkbox" name="sponsorId" checked={searchColumns.sponsorId} onChange={handleCheckboxChange} />
                Sponsor ID
              </label>
            </th>
            <th>
              <label>
                <input type="checkbox" name="sponsorName" checked={searchColumns.sponsorName} onChange={handleCheckboxChange} />
                Sponsor Name
              </label>
            </th>
            <th>Actions</th>
            <th>Date Joined</th>
          </tr>
        </thead>
        <tbody>
          {members.length > 0 ? (members.map((member, index) => (
            <tr key={member.id}>
              <td data-label="S.No." className="sno-column">{(currentPage - 1) * itemsPerPage + index + 1}</td>
              <td data-label="ID">{member.id}</td>
              <td data-label="Member ID">{member.member_id}</td>
              <td data-label="Member Name">{member.member_name || 'N/A'}</td>
              <td data-label="Sponsor ID">{member.sponsor_id || 'N/A'}</td>
              <td data-label="Sponsor Name">{member.sponsor_name || 'N/A'}</td>
              <td data-label="Actions">
                {!member.member_id.includes('-') && (
                  <button onClick={() => handleGeneratePdf(member)} className="action-button download-pdf-button">Download PDF</button>
                )}
              </td>
              <td data-label="Date Joined">{member.joining_date ? new Date(member.joining_date).toLocaleDateString() : 'N/A'}</td>
            </tr>
          ))) : (
            <tr>
              <td colSpan={8} style={{ textAlign: 'center' }}>No members found.</td>
            </tr>
          )}
        </tbody>
      </table>
      {totalMembers > 0 && (
        <div className="pagination-controls">
          <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
            &laquo; First
          </button>
          <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
            &lsaquo; Previous
          </button>
          <span className="page-info">Page {currentPage} of {totalPages}</span>
          <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
            Next &rsaquo;
          </button>
          <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>
            Last &raquo;
          </button>
          <form onSubmit={handleGoToPageSubmit} className="go-to-page-form">
            <input
              type="number"
              inputMode="numeric"
              value={goToPage}
              onChange={(e) => setGoToPage(e.target.value)}
              className="page-input"
              min="1"
              max={totalPages}
              placeholder="Page"
            />
            <button type="submit">Go</button>
          </form>
          <div className="items-per-page-selector">
            <span>Items per page:</span>
            <select value={itemsPerPage} onChange={e => setItemsPerPage(Number(e.target.value))} className="filter-select">              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
              <option value={1000}>1000</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembersListPage;